import { useState, useCallback, useEffect } from 'react'
import { X, Filter } from 'lucide-react'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/SearchableSelect'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import { useUserRole } from '@/hooks/useUserRole'
import { getCompanies, getProperties } from '../services/userManagementApi'
import type { UserFilters as UserFiltersType, UserFiltersProps } from '../types'
import {
  ORGANIZATION_TYPE_OPTIONS,
  ACCOUNT_STATUS_OPTIONS,
  PROPERTY_STATUS_OPTIONS,
  USER_MANAGEMENT_POLICIES,
} from '../constants'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import styles from '../styles/userFilters.module.css'

function isFilterValueSelected(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return false
  if (Array.isArray(value)) return value.length > 0
  return true
}

function filterSelectWrapClass(value: unknown): string {
  const filled = isFilterValueSelected(value)
  return [
    'relative',
    styles.pillFilterWrapperVertical,
    filled ? styles.pillFilterFilled : styles.pillFilterEmpty,
  ].join(' ')
}

/**
 * UserFilters drawer – matches CompanyFilters (PMC) SearchableSelect + Done button treatment
 */
export function UserFilters({ filters, onFiltersChange, onReset, isOpen, onClose }: UserFiltersProps) {
  const { hasPolicy, isCompanyRole, isPropertyRole } = useUserRole()
  const canFilterByPropertyStatus = hasPolicy([USER_MANAGEMENT_POLICIES.ALL_REF_FILTER])

  const [companyOptions, setCompanyOptions] = useState<SearchableSelectOption[]>([])
  const [propertyOptions, setPropertyOptions] = useState<SearchableSelectOption[]>([])
  const [companiesLoading, setCompaniesLoading] = useState(false)
  const [propertiesLoading, setPropertiesLoading] = useState(false)

  const loadCompanies = useCallback(async (q?: string) => {
    setCompaniesLoading(true)
    try {
      const list = await getCompanies(q, filters.listed || 'all')
      const options = list.map((c) => ({ value: String(c.company_id), label: c.company_name }))
      // If the currently-selected company is not in the returned list, seed it so the name always shows.
      const selectedCid = filters.company_id
      if (selectedCid && !options.some((o) => String(o.value) === selectedCid)) {
        const savedName = filters.company_name
        if (savedName && !/^\d+$/.test(savedName)) {
          // Saved name is a real name (not a raw ID) — use it directly.
          options.unshift({ value: selectedCid, label: savedName })
        } else {
          // Saved name is missing or looks like a raw ID — search the API for the real name.
          try {
            const targeted = await getCompanies(selectedCid, filters.listed || 'all')
            const found = targeted.find((c) => String(c.company_id) === selectedCid)
            if (found) {
              options.unshift({ value: selectedCid, label: found.company_name })
              onFiltersChange({ company_name: found.company_name })
            }
          } catch { /* ignore */ }
        }
      }
      setCompanyOptions(options)
      if (options.length === 1 && filters.company_id !== options[0].value) {
        onFiltersChange({ company_id: options[0].value, company_name: options[0].label })
      }
    } catch {
      setCompanyOptions([])
    } finally {
      setCompaniesLoading(false)
    }
  }, [filters.listed, filters.company_id, filters.company_name, onFiltersChange])

  const loadProperties = useCallback(
    async (q?: string, cid?: string) => {
      setPropertiesLoading(true)
      try {
        const companyId = cid ?? filters.company_id ?? ''
        const list = await getProperties(q, companyId, filters.listed || 'all')
        const options = list.map((p) => ({ value: String(p.property_id), label: p.property_name }))
        // If the currently-selected property is not in the returned list, seed it so the name always shows.
        const selectedPid = filters.property_id
        if (selectedPid && !options.some((o) => String(o.value) === selectedPid)) {
          const savedName = filters.property_name
          if (savedName && !/^\d+$/.test(savedName)) {
            // Saved name is a real name — use it directly.
            options.unshift({ value: selectedPid, label: savedName })
          } else {
            // Saved name is missing or looks like a raw ID — search the API for the real name.
            try {
              const targeted = await getProperties(selectedPid, companyId, filters.listed || 'all')
              const found = targeted.find((p) => String(p.property_id) === selectedPid)
              if (found) {
                options.unshift({ value: selectedPid, label: found.property_name })
                onFiltersChange({ property_name: found.property_name })
              }
            } catch { /* ignore */ }
          }
        }
        setPropertyOptions(options)
      } catch {
        setPropertyOptions([])
      } finally {
        setPropertiesLoading(false)
      }
    },
    [filters.company_id, filters.property_id, filters.property_name, filters.listed, onFiltersChange]
  )

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [isOpen, onClose])

  useBodyScrollLock(isOpen)

  // Seed the selected company/property option from persisted filters so the saved name (not the raw id) shows on revisit.
  // Guards against raw numeric IDs being stored as the label (e.g. from stale session storage).
  useEffect(() => {
    if (filters.company_id) {
      const label = filters.company_name && !/^\d+$/.test(filters.company_name)
        ? filters.company_name
        : null
      if (label) {
        setCompanyOptions((prev) =>
          prev.some((o) => String(o.value) === filters.company_id)
            ? prev
            : [{ value: filters.company_id!, label }, ...prev]
        )
      }
    }
    if (filters.property_id) {
      const label = filters.property_name && !/^\d+$/.test(filters.property_name)
        ? filters.property_name
        : null
      if (label) {
        setPropertyOptions((prev) =>
          prev.some((o) => String(o.value) === filters.property_id)
            ? prev
            : [{ value: filters.property_id!, label }, ...prev]
        )
      }
    }
  }, [filters.company_id, filters.company_name, filters.property_id, filters.property_name])

  /** Angular: fire on every 2nd character */
  const handleCompanySearch = useCallback(
    (q: string) => {
      if (q.length > 0 && q.length % 2 !== 0) return
      void loadCompanies(q)
    },
    [loadCompanies]
  )

  const handlePropertySearch = useCallback(
    (q: string) => {
      if (q.length > 0 && q.length % 2 !== 0) return
      void loadProperties(q)
    },
    [loadProperties]
  )

  const handleCompanyChange = (val: string | string[]) => {
    const cid = typeof val === 'string' ? val : val[0] ?? ''
    const company_name = String(companyOptions.find((o) => String(o.value) === cid)?.label ?? '')
    onFiltersChange({ company_id: cid, company_name, page: 1 })
    void loadProperties(undefined, cid)
  }

  if (!isOpen) return null

  return (
    <>
      <div
        className="fixed inset-0 z-[2147483646] bg-slate-900/20 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden
        data-testid="um-filters-overlay"
      />

      <div
        className="fixed right-0 top-0 z-[2147483647] flex h-full w-full max-w-sm flex-col bg-white shadow-2xl"
        data-testid="um-filters-drawer"
      >
        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-5 py-4">
          <div className="flex items-center gap-2 text-slate-800">
            <Filter className="h-5 w-5 shrink-0 text-[rgb(21_151_255)]" aria-hidden />
            <h2 className="text-base font-semibold">Filter Users</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-all"
            aria-label="Close filters"
            data-testid="um-filters-close-btn"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-500">
                Organization Type
              </label>
              <div className={filterSelectWrapClass(filters.organization_type)}>
                <SearchableSelect
                  triggerDataTestId="um-filters-org-type-select"
                  placeholder="All"
                  searchable={false}
                  options={ORGANIZATION_TYPE_OPTIONS.map((opt) => ({
                    value: opt.value,
                    label: opt.label,
                  }))}
                  value={filters.organization_type ?? ''}
                  onChange={(val) => {
                    const v = Array.isArray(val) ? val[0] : val
                    onFiltersChange({
                      organization_type: (v || undefined) as UserFiltersType['organization_type'],
                      page: 1,
                    })
                  }}
                />
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-500">Company</label>
              <div className={filterSelectWrapClass(filters.company_id)}>
                <SearchableSelect
                  options={companyOptions}
                  value={filters.company_id ?? ''}
                  onChange={handleCompanyChange}
                  placeholder="All Company"
                  searchPlaceholder="Search companies…"
                  onOpen={() => void loadCompanies()}
                  onSearch={handleCompanySearch}
                  immediateSearchOnPaste
                  onPasteSearch={(q) => void loadCompanies(q)}
                  loading={companiesLoading}
                  disabled={isCompanyRole || isPropertyRole}
                  className="w-full"
                  triggerDataTestId="um-filters-company-select"
                />
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-500">Property</label>
              <div className={filterSelectWrapClass(filters.property_id)}>
                <SearchableSelect
                  options={propertyOptions}
                  value={filters.property_id ?? ''}
                  onChange={(val) => {
                    const pid = typeof val === 'string' ? val : val[0] ?? ''
                    const property_name = String(
                      propertyOptions.find((o) => String(o.value) === pid)?.label ?? ''
                    )
                    onFiltersChange({ property_id: pid, property_name, page: 1 })
                  }}
                  placeholder="All Properties"
                  searchPlaceholder="Search properties…"
                  onOpen={() => void loadProperties()}
                  onSearch={handlePropertySearch}
                  immediateSearchOnPaste
                  onPasteSearch={(q) => void loadProperties(q)}
                  loading={propertiesLoading}
                  disabled={isPropertyRole}
                  className="w-full"
                  triggerDataTestId="um-filters-property-select"
                />
              </div>
            </div>

            {canFilterByPropertyStatus && (
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-500">
                  Property Status
                </label>
                <div className={filterSelectWrapClass(filters.listed)}>
                  <SearchableSelect
                    triggerDataTestId="um-filters-property-status-select"
                    placeholder="All Property Statuses"
                    searchable={false}
                    options={PROPERTY_STATUS_OPTIONS.map((opt) => ({
                      value: opt.value,
                      label: opt.label,
                    }))}
                    value={filters.listed ?? ''}
                    onChange={(val) => {
                      const v = Array.isArray(val) ? val[0] : val
                      onFiltersChange({
                        listed: (v || undefined) as UserFiltersType['listed'],
                        page: 1,
                      })
                    }}
                  />
                </div>
              </div>
            )}

            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-500">
                Account Status
              </label>
              <div className={filterSelectWrapClass(filters.account_status)}>
                <SearchableSelect
                  triggerDataTestId="um-filters-account-status-select"
                  placeholder="All User Statuses"
                  searchable={false}
                  options={ACCOUNT_STATUS_OPTIONS.map((opt) => ({
                    value: opt.value,
                    label: opt.label,
                  }))}
                  value={filters.account_status ?? ''}
                  onChange={(val) => {
                    const v = Array.isArray(val) ? val[0] : val
                    onFiltersChange({
                      account_status: (v || undefined) as UserFiltersType['account_status'],
                      page: 1,
                    })
                  }}
                />
              </div>
            </div>
          </div>

          <div
            className="shrink-0 border-t border-slate-100 bg-slate-50 p-4 sm:p-6 flex flex-wrap items-center justify-between gap-4"
            data-testid="um-filters-footer"
          >
            <button
              type="button"
              data-testid="um-filters-reset-btn"
              onClick={() => onReset()}
              className="filter-drawer-reset-all"
            >
              Reset All
            </button>
            <button
              type="button"
              data-testid="um-filters-done-btn"
              onClick={onClose}
              className={cn(primaryEntityActionButtonClass, 'flex-1')}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
